import type { Editor, JSONContent } from "@tiptap/core"; // Added JSONContent
import type { Mark } from "@tiptap/pm/model";
import { TextSelection, type EditorState } from "@tiptap/pm/state";
import { v4 as uuid } from "uuid";

import type { HighlightColors } from "@/components/nw-highlight/models";
// Corrected import path for NoteOptions and NoteType
import { NoteOptions, NoteType } from "@/components/nw-note/models";
import { NWSectionStatus } from "@/components/nw-section/models";
import type { NWHighlight } from "@/components/nw-highlight";
import { useAnalyticsService } from "@/services/analyticsService";
import { Store } from "@/store";

import { HighlightInteractionEvent } from "./extensions/highlight/events";
import { getElementsForGroup } from "./utils/nodes";
import { getSelection } from "./utils/selection";

export class EditorController {
  constructor(private readonly editor: Editor) {}

  /**
   * Adds a highlight to the selected text and returns the added highlight node.
   * @param color - The color of the highlight to be added.
   * @returns The added highlight node or null if the operation failed.
   */
  public addHighlight(color: HighlightColors): Mark | undefined {
    const selection = this.editor.view.state.selection;
    const highlight = this.editor.commands.setHighlight({ color }) as unknown as Mark;

    if (highlight == null) {
      return undefined;
    }

    const group = highlight.attrs.group;
    Store.editor.setActiveHighlightGroup(group);
    Store.editor.setPreferredHighlightColor(color);

    // Clear user selection
    this.editor.commands.blur();

    const addedEvent = new CustomEvent(HighlightInteractionEvent.HIGHLIGHT_ADDED, {
      detail: { group },
    });

    this.editor.view.dom.dispatchEvent(addedEvent);

    // We manually trigger this as 'tap to click' on osx does not trigger the same event chain to cause the tiptap selection to be cleared on click
    this.editor.commands.setTextSelection(selection.to);

    useAnalyticsService().trackEditorHighlightAdded(color, this.getSelectedText(group));
    return highlight as unknown as Mark;
  }

  /**
   * Changes the color of a highlight with the specified group ID.
   * @param groupId - The ID of the highlight group to change.
   * @param color - The new color for the highlight.
   * @returns True if the operation was successful, false otherwise.
   */
  public changeHighlightColor(groupId: string | undefined, color: HighlightColors): boolean {
    if (groupId == null) {
      return false;
    }

    Store.editor.setActiveHighlightGroup(groupId);
    Store.editor.setPreferredHighlightColor(color);

    const res = this.editor.chain().focus().changeHighlightColor(groupId, color).run();

    const event = new CustomEvent(HighlightInteractionEvent.HIGHLIGHT_INTERACTED, {
      detail: { group: groupId },
    });
    this.editor.view.dom.dispatchEvent(event);
    return res;
  }

  /**
   * Add a highlight of the given color or change an existing highlight to a given color
   * @param color - Target color of the resulting highlight.
   */
  public addOrChangeHighlightColor(color: HighlightColors): boolean {
    const group = Store.editor.activeHighlightGroup.value;
    if (group == null) {
      this.addHighlight(color);
      return true;
    }

    return this.changeHighlightColor(group, color);
  }

  /**
   * Removes the highlight for the selected text.
   * @param group - The group of the highlight to be removed.
   * @returns The remove operation result.
   */
  public removeHighlight(group: string | undefined): boolean {
    if (group == null) {
      return false;
    }
    const res = this.editor.chain().focus().unsetHighlight(group).run();
    Store.editor.setActiveHighlightGroup(undefined);
    this.closeBubbleMenu();

    useAnalyticsService().trackEditorHighlightRemoved(this.getSelectedText(group));
    return res;
  }

  /**
   * Adds a note to the end of the currently selected node
   * @param noteType - The type of the note to be added.
   * @param group - The group of the highlight to be added.
   * @param options
   * @returns The add operation result.
   */
  public addNote<T extends keyof NoteOptions>(noteType: T, group: string | undefined, options: NoteOptions[T]) {
    const highlightContext = this.getSelectedText(group);
    const surroundingContext = this.getSurroundingContext(this.editor.view.state);

    if (group == null) {
      return;
    }

    const noteId = uuid();
    const isAsyncAction =
      noteType === NoteType.SIMPLIFY ||
      noteType === NoteType.EXPLAIN ||
      noteType === NoteType.ASK ||
      noteType === NoteType.EXAMPLES;

    // Define initial empty content for the note's internal editor
    const initialContent: JSONContent = { type: "doc", content: [{ type: "paragraph" }] };

    this.editor
      .chain()
      .focus()
      .command(({ editor, view, tr }) => {
        const selection = getSelection(editor, view);

        if (!selection) {
          return false;
        }

        const { $head } = selection;
        let endPos = $head.end();

        // Find the appropriate position to insert the note, avoiding nesting inside another note
        let currentDepth = $head.depth;
        while (currentDepth >= 0) {
          const nodeAtDepth = $head.node(currentDepth);
          if (nodeAtDepth.type.name === "nw-note") {
            // If we are inside a note, insert after it
            endPos = $head.after(currentDepth);
            break;
          }
          currentDepth--;
        }

        tr.setSelection(TextSelection.create(tr.doc, endPos));
        return true;
      })
      .createNWNote({
        id: noteId,
        notetype: noteType,
        highlightContext,
        surroundingContext,
        loading: isAsyncAction,
        group,
        content: initialContent,
        ...(options as object),
      })
      .run();

    switch (noteType) {
      case NoteType.BLANK:
        useAnalyticsService().trackEditorAIAction("createBlank", group, highlightContext, surroundingContext, noteId);
        break;
      case NoteType.EXPLAIN:
        this.editor.commands.explain(group, highlightContext, surroundingContext, noteId);
        break;
      case NoteType.SIMPLIFY:
        this.editor.commands.simplify(group, highlightContext, surroundingContext, noteId);
        break;
      case NoteType.EXAMPLES:
        this.editor.commands.examples(group, highlightContext, surroundingContext, noteId);
        break;
      case NoteType.ASK: {
        const opts = options as NoteOptions[NoteType.ASK];
        this.editor.commands.ask(group, highlightContext, surroundingContext, noteId, opts.query);
        break;
      }
    }

    this.setActiveHighlightGroup(group, false);
    this.closeBubbleMenu();
  }

  /**
   * Updates the heading of a note.
   * @param id - The ID of the note to be updated.
   * @param heading - The new heading for the note.
   * @returns The update operation result.
   */
  public updateNoteHeading(id: string, heading: string) {
    return this.editor.chain().updateNoteHeading(id, heading).run();
  }

  /**
   * Updates the content attribute of a note node.
   * @param id - The ID of the note to be updated.
   * @param content - The new content (JSON) for the note.
   * @returns The update operation result.
   */
  public updateNoteContent(id: string, content: JSONContent) {
    return this.editor.chain().updateNoteContent(id, content).run();
  }

  /**
   * Simplifies the selected text and returns the simplification operation result.
   * @param noteId - The ID of the note to be simplified.
   * @param group - The group of the highlight to be simplified.
   * @returns The simplification operation result.
   */
  public simplify(noteId: string, group: string | undefined) {
    if (group == null) {
      return false;
    }

    const highlightContext = this.getSelectedText(group);
    const surroundingContext = this.getSurroundingContext(this.editor.view.state);

    return this.editor.commands.simplify(group, highlightContext, surroundingContext, noteId);
  }

  /**
   * Explains the selected text and returns the explanation operation result.
   * @param noteId - The ID of the note to be explained.
   * @param group - The group of the highlight to be explained.
   * @returns The explanation operation result.
   */
  public explain(noteId: string, group: string | undefined) {
    if (group == null) {
      return false;
    }

    const highlightContext = this.getSelectedText(group);
    const surroundingContext = this.getSurroundingContext(this.editor.view.state);

    return this.editor.commands.explain(group, highlightContext, surroundingContext, noteId);
  }

  /**
   * Generates examples for the selected text and returns the examples operation result.
   * @param noteId - The ID of the note to be explained.
   * @param group - The group of the highlight to be explained.
   * @returns The explanation operation result.
   */
  public examples(noteId: string, group: string | undefined) {
    if (group == null) {
      return false;
    }

    const highlightContext = this.getSelectedText(group);
    const surroundingContext = this.getSurroundingContext(this.editor.view.state);

    return this.editor.commands.examples(group, highlightContext, surroundingContext, noteId);
  }

  public ask(noteId: string, group: string | undefined, query: string) {
    if (group == null) {
      return false;
    }

    const highlightContext = this.getSelectedText(group);
    const surroundingContext = this.getSurroundingContext(this.editor.view.state);

    return this.editor.commands.ask(group, highlightContext, surroundingContext, noteId, query);
  }

  /**
   * Deletes a note by its ID.
   * @param id - The ID of the note to be deleted.
   * @returns The delete operation result.
   */
  public deleteNote(id: string) {
    return this.editor.chain().deleteNote(id).run();
  }

  /**
   * Sets the active highlight group.
   * @param group - The group of the highlight to be set as active.
   * @param toActive - Whether to set the highlight group as active or not.
   * @returns The set active highlight group operation result.
   */
  public setActiveHighlightGroup(group: string | undefined, toActive: boolean) {
    if (group == null) {
      return false;
    }

    if (toActive) {
      return this.editor.commands.activateHighlightGroup(group);
    }

    return this.editor.commands.deactivateHighlightGroup(group);
  }

  /**
   * Closes the bubble menu.
   */
  public closeBubbleMenu() {
    const event = new CustomEvent(HighlightInteractionEvent.CLOSE_BUBBLE_MENU);
    this.editor.view.dom.dispatchEvent(event);
  }

  /**
   * Sets the content of the editor.
   * @param content - The content to be set.
   */
  public setSectionContent(sectionId: string, content: string) {
    this.editor.commands.setSectionContent(sectionId, content);
  }

  /**
   * Unlocks a pro section.
   * @param sectionId - The ID of the section to be unlocked.
   */
  public unlockProSection(sectionId: string) {
    this.editor.commands.updateProSection(sectionId, { status: NWSectionStatus.UNLOCKED });
  }

  /**
   * Toggles a bookmark on a node.
   * @param editorInstanceId - The ID of the editor
   * @param nodeId - The ID of the node to toggle the bookmark on.
   */
  public toggleBookmark(editorInstanceId: string, nodeId: string) {
    this.editor.chain().toggleBookmark(nodeId).run();
    Store.editor.setActiveInstanceId(editorInstanceId);

    useAnalyticsService().trackBookmarkToggled(Store.project.id.value!, nodeId, "added");
  }

  /**
   * Gets the surrounding context of the current selection.
   * @param state - The editor state.
   * @returns The surrounding context.
   */
  private getSurroundingContext(state: EditorState): string {
    const selection = getSelection(this.editor, this.editor.view);

    if (selection == null) {
      return "";
    }

    const { $from, $to } = selection;

    const contextStart = $from.start($from.depth);
    const contextEnd = $to.end($to.depth);

    return state.doc.textBetween(contextStart, contextEnd);
  }

  /**
   * Gets the selected text for the current selection.
   * @param group - The group of the highlight to get the selected text for.
   * @returns The selected text.
   */
  private getSelectedText(group: string | undefined): string {
    const { view, state } = this.editor;

    if (group) {
      const highlights = getElementsForGroup<NWHighlight>(view, group, "nw-highlight");
      const text = highlights.map(highlight => highlight.textContent).join(" ");

      return text;
    }

    const selection = getSelection(this.editor, view);
    if (selection == null) {
      return "";
    }
    return state.doc.textBetween(selection.from, selection.to, ". ");
  }
}
